import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import React, { useRef, useState } from 'react';

import styled from 'styled-components';
import { PLATE_MEASUREMENTS } from '../../../../constants/plate_measurements';
import { ICOP } from '../../../../data/types';
import { nineFiveConfidenceEllipse } from '../../../../utils/nineFiveConfidenceEllipse';
import { Column } from '../../../layout/Column';
import { Card } from '../../card/Card';
import { CardHeader } from '../../card/CardHeader';
import { EPdfMode, isPdf, shouldInvert } from '../../pdf/Pdf';
import { GraphContainer } from '../GraphContainer';
import { PhotoExportMenu } from '../PhotoExportMenu';
import { CrossHairs } from './CrossHairs';
import { Ellipse } from './Ellipse';
import { Path } from './Path';
import { Plate } from './Plate';
import { Zone } from './Zone';
import { IBoxConstraints, IPoint, WhichPlate } from './interfaces';

// Fallback dimensions used when DOM measurements aren't available (e.g., PDF mode
// or initial render). These values provide a reasonable aspect ratio for the
// force plate visualization while fitting within typical PDF page widths.
const PDF_FALLBACK_WIDTH = 400;
const PDF_FALLBACK_HEIGHT = 280;

interface IProps {
  cop: ICOP;
  style?: React.CSSProperties;
  pdfMode: EPdfMode;
}
export function COPGraph(props: IProps) {
  const [copPoints, setCOPPoints] = useState<COPPoints>();
  const [boxConstraints, setBoxConstraints] = useState<IBoxConstraints>();
  const boxConstraintsRef = useRef(null);
  const exportContainerRef = useRef<HTMLDivElement>(null);

  const onExportPhoto = (backgroundColor?: string): void => {
    if (!exportContainerRef.current) return;
    html2canvas(exportContainerRef.current, {
      backgroundColor,
    })
      .then((canvas: HTMLCanvasElement): void => {
        saveAs(canvas.toDataURL(), 'Center-of-Pressure.png');
      })
      .catch((): void => {
        alert('Failed to export image. Please try again.');
      });
  };

  React.useEffect(() => {
    const pathLeft: IPoint[] = [];
    const pathRight: IPoint[] = [];

    for (let i = 0; i < props.cop.copX.length; i++) {
      if (props.cop.leftCOPX[i] !== null && props.cop.leftCOPY[i] !== null) {
        pathLeft.push({ x: props.cop.leftCOPX[i], y: props.cop.leftCOPY[i] });
      }
      if (props.cop.rightCOPX[i] !== null && props.cop.rightCOPY[i] !== null) {
        pathRight.push({
          x: props.cop.rightCOPX[i],
          y: props.cop.rightCOPY[i],
        });
      }
    }

    const points = new COPPoints(
      pathLeft.length > 0 ? nineFiveConfidenceEllipse(pathLeft) : [],
      pathRight.length > 0 ? nineFiveConfidenceEllipse(pathRight) : [],
      pathLeft,
      pathRight
    );
    setCOPPoints(points);

    // In PDF mode, use fixed dimensions since DOM measurements aren't available
    // Otherwise measure the container
    const measuredWidth = boxConstraintsRef.current?.clientWidth ?? 0;
    const measuredHeight = boxConstraintsRef.current?.clientHeight ?? 0;
    const boxConstraints: IBoxConstraints = {
      maxWidth: isPdf(props.pdfMode) || measuredWidth === 0 ? PDF_FALLBACK_WIDTH : measuredWidth,
      maxHeight: isPdf(props.pdfMode) || measuredHeight === 0 ? PDF_FALLBACK_HEIGHT : measuredHeight,
    };
    setBoxConstraints(boxConstraints);
  }, [props.cop?.leftCOPX?.length, props.cop?.rightCOPX?.length, props.pdfMode]);

  const plateGap = 20;

  const scaleFactor = Math.min(
    (boxConstraints?.maxWidth ?? 0 - 10 - plateGap) /
      PLATE_MEASUREMENTS.SINGLE_X,
    (boxConstraints?.maxHeight ?? 0 - 10) / PLATE_MEASUREMENTS.Y,
    1.0
  );

  const containingBox: IBoxConstraints = {
    maxWidth: PLATE_MEASUREMENTS.SINGLE_X * scaleFactor,
    maxHeight: PLATE_MEASUREMENTS.Y * scaleFactor,
  };

  const singlePlateBox: IBoxConstraints = {
    maxWidth: PLATE_MEASUREMENTS.SINGLE_X * scaleFactor,
    maxHeight: PLATE_MEASUREMENTS.Y * scaleFactor,
  };

  const inverse = shouldInvert(props.pdfMode);

  return (
    <ForceTimeCard
      style={{
        ...props.style,
        flex: 3,
      }}
    >
      <Column>
        <CardHeader>
          <HeaderLeft>
            <h6>Center of Pressure</h6>
          </HeaderLeft>
          <HeaderRight>
            {!isPdf(props.pdfMode) && (
              <PhotoExportMenu onExportPhoto={onExportPhoto} />
            )}
          </HeaderRight>
        </CardHeader>
        <GraphContainer ref={boxConstraintsRef} inverse={inverse}>
          <div
            ref={exportContainerRef}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {copPoints?.whichPlate === WhichPlate.both ? (
              <React.Fragment>
                <div
                  style={{
                    marginRight: `${plateGap * scaleFactor}px`,
                    width: `${containingBox.maxWidth}px`,
                    height: `${containingBox.maxHeight}px`,
                    position: 'relative',
                  }}
                >
                  <Plate
                    boxConstraints={singlePlateBox}
                    label={WhichPlate.left}
                    inverse={inverse}
                  />
                  <CrossHairs boxConstraints={singlePlateBox} inverse={inverse} />
                  <Zone percent={25} boxConstraints={singlePlateBox} inverse={inverse} />
                  <Zone percent={50} boxConstraints={singlePlateBox} inverse={inverse} />
                  <Zone percent={75} boxConstraints={singlePlateBox} inverse={inverse} />
                  <Path
                    boxConstraints={singlePlateBox}
                    scaleFactor={scaleFactor}
                    points={copPoints?.pathLeft ?? []}
                    inverse={inverse}
                  />
                  <Ellipse
                    boxConstraints={singlePlateBox}
                    scaleFactor={scaleFactor}
                    points={copPoints?.ellipseLeft ?? []}
                    inverse={inverse}
                  />
                </div>
                <div
                  style={{
                    position: 'relative',
                    width: `${containingBox.maxWidth}px`,
                    height: `${containingBox.maxHeight}px`,
                  }}
                >
                  <Plate
                    boxConstraints={singlePlateBox}
                    label={WhichPlate.right}
                    inverse={inverse}
                  />
                  <CrossHairs boxConstraints={singlePlateBox} inverse={inverse} />
                  <Zone percent={25} boxConstraints={singlePlateBox} inverse={inverse} />
                  <Zone percent={50} boxConstraints={singlePlateBox} inverse={inverse} />
                  <Zone percent={75} boxConstraints={singlePlateBox} inverse={inverse} />
                  <Path
                    boxConstraints={singlePlateBox}
                    scaleFactor={scaleFactor}
                    points={copPoints?.pathRight ?? []}
                    inverse={inverse}
                  />
                  <Ellipse
                    boxConstraints={singlePlateBox}
                    scaleFactor={scaleFactor}
                    points={copPoints?.ellipseRight ?? []}
                    inverse={inverse}
                  />
                </div>
              </React.Fragment>
            ) : (
              <div
                style={{
                  width: `${containingBox.maxWidth}px`,
                  height: `${containingBox.maxHeight}px`,
                  position: 'relative',
                }}
              >
                <Plate
                  boxConstraints={singlePlateBox}
                  label={
                    copPoints?.whichPlate === WhichPlate.left
                      ? WhichPlate.left
                      : WhichPlate.right
                  }
                  inverse={inverse}
                />
                <CrossHairs boxConstraints={singlePlateBox} inverse={inverse} />
                <Zone percent={25} boxConstraints={singlePlateBox} inverse={inverse} />
                <Zone percent={50} boxConstraints={singlePlateBox} inverse={inverse} />
                <Zone percent={75} boxConstraints={singlePlateBox} inverse={inverse} />
                <Path
                  boxConstraints={singlePlateBox}
                  scaleFactor={scaleFactor}
                  points={
                    copPoints?.whichPlate === WhichPlate.left
                      ? copPoints?.pathLeft ?? []
                      : copPoints?.pathRight ?? []
                  }
                  inverse={inverse}
                />
                <Ellipse
                  boxConstraints={singlePlateBox}
                  scaleFactor={scaleFactor}
                  points={
                    copPoints?.whichPlate === WhichPlate.left
                      ? copPoints?.ellipseLeft ?? []
                      : copPoints?.ellipseRight ?? []
                  }
                  inverse={inverse}
                />
              </div>
            )}
          </div>
        </GraphContainer>
      </Column>
    </ForceTimeCard>
  );
}

class COPPoints {
  public whichPlate: WhichPlate;
  constructor(
    public ellipseLeft: IPoint[],
    public ellipseRight: IPoint[],
    public pathLeft: IPoint[],
    public pathRight: IPoint[]
  ) {
    if (ellipseLeft.length > 0 && ellipseRight.length > 0) {
      this.whichPlate = WhichPlate.both;
    } else if (ellipseLeft.length > 0) {
      this.whichPlate = WhichPlate.left;
    } else if (ellipseRight.length > 0) {
      this.whichPlate = WhichPlate.right;
    } else {
      this.whichPlate = WhichPlate.both;
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ForceTimeCard = styled(Card as any)`
  padding: 0px;
  margin-top: var(--grid-gap);
  min-height: var(--graph-min-height);
`;
const HeaderLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const HeaderRight = styled.div`
  flex: 0;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;
